import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Phone,
  PhoneCall,
  PhoneMissed,
  PhoneOff,
  PhoneIncoming,
  PhoneOutgoing,
  Ticket,
  AlertTriangle,
  Clock,
  Users,
  TrendingUp,
  Activity,
  CheckCircle,
  XCircle,
  Flame,
  Zap,
  Star,
  Award,
  Target,
  Timer,
  RefreshCw,
  Maximize,
  Volume2,
  Building2,
  Database,
  Server,
  HeartPulse,
  Crown,
  X,
  Bell
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import { QueueHealthGauge } from "../components/QueueHealthGauge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const JIRA_URL = "https://equiprsoftware.atlassian.net/browse";

// Default team members - will be overridden by agent mapping from API
const DEFAULT_TEAM_MEMBERS = [
  { id: "simon", name: "Simon Brennan", avatar: "SB", color: "#007AFF" },
  { id: "valentino", name: "Valentino Grosso", avatar: "VG", color: "#30D158" },
  { id: "kevin_carbe", name: "Kevin Carbe", avatar: "KC", color: "#FF9F0A" },
  { id: "jason", name: "Jason Hooper", avatar: "JH", color: "#FF453A" }
];

// Avatar colors for agents
const AVATAR_COLORS = {
  "simon": "#007AFF",
  "valentino": "#30D158",
  "kevin_carbe": "#FF9F0A",
  "jason": "#FF453A",
  "kevin_russell": "#AF52DE",
  "anthony": "#5856D6",
  "emergency": "#FF2D55"
};

// Gamification point values
const POINTS = {
  TICKET_RESOLVED: 10,
  FIRST_RESPONSE_SLA: 5,
  COMPLETE_DOCUMENTATION: 3,
  PROPER_PRIORITY: 2,
  CUSTOMER_CONFIRMATION: 3,
  CALLBACK_30MIN: 5,
  TICKET_FROM_CALL_10MIN: 3,
  ZERO_MISSED_CALLBACKS_BONUS: 10,
  SLA_MET: 5,
  SLA_EARLY_BONUS: 3,
  SLA_BREACH_PENALTY: -10,
  DAILY_100_SLA: 20,
  NO_IDLE_TICKETS: 10,
  ALL_DOCUMENTED: 10,
  TICKET_CLOSED_NO_CONFIRM: -15,
  MISSING_FIELDS: -3,
  MISSED_CALLBACK_30MIN: -10
};

export default function CommandCentre() {
  const navigate = useNavigate();
  
  // Check authentication on mount
  useEffect(() => {
    const session = localStorage.getItem("equipr_session");
    if (!session) {
      navigate("/login");
      return;
    }
    try {
      const sessionData = JSON.parse(session);
      if (sessionData.expires && new Date(sessionData.expires) < new Date()) {
        localStorage.removeItem("equipr_session");
        navigate("/login");
      }
    } catch (e) {
      localStorage.removeItem("equipr_session");
      navigate("/login");
    }
  }, [navigate]);
  
  const [stats, setStats] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [agents, setAgents] = useState([]);
  const [calls, setCalls] = useState([]);
  const [callsLastFetch, setCallsLastFetch] = useState(null);
  const [apiCallMetrics, setApiCallMetrics] = useState(null);
  const [teamScores, setTeamScores] = useState([]);
  const [companyHealth, setCompanyHealth] = useState(null);
  const [databaseHealth, setDatabaseHealth] = useState(null);
  const [wcfHealth, setWcfHealth] = useState(null);
  const [agentMappings, setAgentMappings] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // VIP Alert State
  const [vipLookup, setVipLookup] = useState({});
  const [seenTicketKeys, setSeenTicketKeys] = useState(new Set());
  const [vipAlerts, setVipAlerts] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = useRef(null);
  
  // VIP Alert Sound - using a simple beep audio URL
  const VIP_ALERT_SOUND = "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp6TdlBUcYqfqpuFZVBkh6GwqZR4XF1+nauzoo5yYGmDl6WejoNrV2F+l6aooouGb1xigZqmqZqGdWJcZ32YqKqYhXNoYHB8mKaimYl5bWFleoSSm5SOgHZtYWRzfYyalJKKgXZsamJreYaPmpaRiYN7cmxqcHl/hIiLi4qIg39+enh1dHV2d3h6e318e3p7enp6eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eXl5eQ==";

  // Play VIP alert sound
  const playVipAlertSound = useCallback(() => {
    if (soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(e => console.log("Audio play failed:", e));
    }
  }, [soundEnabled]);

  // Dismiss a VIP alert
  const dismissVipAlert = useCallback((ticketKey) => {
    setVipAlerts(prev => prev.filter(a => a.key !== ticketKey));
  }, []);

  // Generate mock call data (will be replaced with Maxotel API)
  const generateMockCalls = useCallback(() => {
    const statuses = ["active", "completed", "missed", "callback_pending", "callback_completed"];
    const companies = [
      "Borger Cranes", "Delcon Group", "Metro Construction", "Pacific Mining",
      "Southern Logistics", "Hunter Equipment", "Coastal Rentals", "Allied Services"
    ];
    
    const mockCalls = [];
    const now = Date.now();
    
    // Generate 8-12 calls for today
    const callCount = 8 + Math.floor(Math.random() * 5);
    
    for (let i = 0; i < callCount; i++) {
      const status = statuses[Math.floor(Math.random() * statuses.length)];
      const company = companies[Math.floor(Math.random() * companies.length)];
      const duration = status === "active" ? 30 + Math.floor(Math.random() * 300) : 60 + Math.floor(Math.random() * 600);
      const waitTime = Math.floor(Math.random() * 120);
      const assignedTo = DEFAULT_TEAM_MEMBERS[Math.floor(Math.random() * DEFAULT_TEAM_MEMBERS.length)];
      
      mockCalls.push({
        id: `call-${i}`,
        company,
        phone: `07${Math.floor(1000 + Math.random() * 9000)}${Math.floor(1000 + Math.random() * 9000)}`,
        status,
        duration,
        waitTime,
        assignedTo: assignedTo.id,
        timestamp: now - Math.floor(Math.random() * 28800000), // Random time in last 8 hours
        direction: Math.random() > 0.3 ? "inbound" : "outbound",
        ticketCreated: status !== "active" && Math.random() > 0.3
      });
    }
    
    // Ensure we have at least 1-2 active calls for demo
    if (!mockCalls.some(c => c.status === "active")) {
      mockCalls[0].status = "active";
      mockCalls[0].duration = 60 + Math.floor(Math.random() * 180);
    }
    
    // Sort by timestamp desc
    return mockCalls.sort((a, b) => b.timestamp - a.timestamp);
  }, []);

  // Calculate team scores based on gamification rules with weighting
  const calculateTeamScores = useCallback((ticketData, callData, mappings) => {
    // Build team members from agent mappings, filtering to support agents only
    const supportAgentIds = ["simon", "valentino", "kevin_carbe", "jason"];
    const teamMembers = mappings
      .filter(agent => supportAgentIds.includes(agent.id) && agent.active)
      .map(agent => ({
        id: agent.id,
        name: agent.name,
        avatar: agent.name.split(' ').map(n => n[0]).join('').slice(0, 2),
        color: AVATAR_COLORS[agent.id] || "#007AFF",
        weight: agent.support_weight || 1.0,
        dedication: `${Math.round((agent.support_weight || 1) * 100)}%`
      }));
    
    // If no mappings yet, use defaults
    const members = teamMembers.length > 0 ? teamMembers : DEFAULT_TEAM_MEMBERS.map(m => ({
      ...m,
      weight: 1.0,
      dedication: "100%"
    }));
    
    return members.map(member => {
      // Get member's tickets (exact name match)
      const memberTickets = ticketData.filter(t => 
        t.assignee?.toLowerCase() === member.name.toLowerCase()
      );
      
      // Get member's calls
      const memberCalls = callData.filter(c => c.assignedTo === member.id);
      
      // Calculate raw points
      let rawPoints = 0;
      let ticketsResolved = 0;
      let slaCompliance = 100;
      let callbackCompliance = 100;
      let avgResponseTime = 0;
      
      // Ticket points
      const resolvedTickets = memberTickets.filter(t => 
        t.status?.toLowerCase().includes("resolved") || t.status?.toLowerCase().includes("closed")
      );
      ticketsResolved = resolvedTickets.length;
      rawPoints += ticketsResolved * POINTS.TICKET_RESOLVED;
      
      // SLA points
      const slaBreaches = memberTickets.filter(t => t.sla_breached).length;
      const slaAtRisk = memberTickets.filter(t => t.sla_risk).length;
      rawPoints += (memberTickets.length - slaBreaches) * POINTS.SLA_MET;
      rawPoints += slaBreaches * POINTS.SLA_BREACH_PENALTY;
      
      if (memberTickets.length > 0) {
        slaCompliance = Math.round(((memberTickets.length - slaBreaches) / memberTickets.length) * 100);
      }
      
      // Call/callback points
      const completedCallbacks = memberCalls.filter(c => c.status === "callback_completed").length;
      const missedCallbacks = memberCalls.filter(c => c.status === "callback_pending").length;
      rawPoints += completedCallbacks * POINTS.CALLBACK_30MIN;
      rawPoints += missedCallbacks * POINTS.MISSED_CALLBACK_30MIN;
      
      const totalCallbacks = completedCallbacks + missedCallbacks;
      if (totalCallbacks > 0) {
        callbackCompliance = Math.round((completedCallbacks / totalCallbacks) * 100);
      }
      
      // Zero missed callbacks bonus
      if (missedCallbacks === 0 && memberCalls.length > 0) {
        rawPoints += POINTS.ZERO_MISSED_CALLBACKS_BONUS;
      }
      
      // 100% SLA bonus
      if (slaCompliance === 100 && memberTickets.length > 0) {
        rawPoints += POINTS.DAILY_100_SLA;
      }
      
      // Apply weight multiplier based on support dedication
      // This normalizes scores so part-time support staff can compete fairly
      // E.g., Kevin at 40% dedication gets his points multiplied by 2.5 (1/0.4) to normalize
      const weight = member.weight || 1.0;
      const weightedPoints = Math.round(rawPoints / weight);
      
      // Calculate avg response time (mock)
      avgResponseTime = 5 + Math.floor(Math.random() * 25);
      
      // Determine status
      const activeCalls = memberCalls.filter(c => c.status === "active");
      const status = activeCalls.length > 0 ? "on_call" : 
                    memberTickets.length > 5 ? "busy" : "available";
      
      return {
        ...member,
        rawPoints: Math.max(0, rawPoints),
        points: Math.max(0, weightedPoints),
        ticketsResolved,
        openTickets: memberTickets.filter(t => !t.status?.toLowerCase().includes("resolved") && !t.status?.toLowerCase().includes("closed")).length,
        slaCompliance,
        callbackCompliance,
        avgResponseTime,
        status,
        activeCall: activeCalls[0] || null,
        trend: Math.random() > 0.3 ? "up" : "down" // Mock trend
      };
    }).sort((a, b) => b.points - a.points);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, ticketsRes, agentsRes, callsRes, companyHealthRes, dbHealthRes, agentMappingsRes, vipRes, wcfHealthRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/tickets`),
        axios.get(`${API}/agents/workload`),
        axios.get(`${API}/calls`),
        axios.get(`${API}/companies/health`),
        axios.get(`${API}/databases/health`),
        axios.get(`${API}/agents/mapping`),
        axios.get(`${API}/clients/vip-lookup`),
        axios.get(`${API}/wcf/health`).catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      const ticketData = ticketsRes.data.tickets || [];
      setTickets(ticketData);
      setAgents(agentsRes.data.agents || []);
      setCompanyHealth(companyHealthRes.data);
      setDatabaseHealth(dbHealthRes.data);
      setWcfHealth(wcfHealthRes.data);
      setAgentMappings(agentMappingsRes.data.agents || []);
      
      // Store VIP lookup
      const vipLookupData = vipRes.data.vip_lookup || {};
      setVipLookup(vipLookupData);
      
      // Check for new VIP tickets and create alerts
      const newVipAlerts = [];
      ticketData.forEach(ticket => {
        if (!seenTicketKeys.has(ticket.key)) {
          const orgKey = (ticket.organization || "").toLowerCase();
          const clientInfo = vipLookupData[orgKey];
          if (clientInfo?.is_vip) {
            // This is a new VIP ticket!
            newVipAlerts.push({
              key: ticket.key,
              summary: ticket.summary,
              organization: ticket.organization,
              priority: ticket.priority,
              timestamp: new Date().toISOString()
            });
          }
        }
      });
      
      // Update seen tickets
      setSeenTicketKeys(prev => {
        const updated = new Set(prev);
        ticketData.forEach(t => updated.add(t.key));
        return updated;
      });
      
      // Add new VIP alerts
      if (newVipAlerts.length > 0) {
        setVipAlerts(prev => [...newVipAlerts, ...prev].slice(0, 5));
        playVipAlertSound();
      }
      
      // Use real Maxotel call data
      const callsData = callsRes.data;
      setApiCallMetrics(callsData.metrics || null);
      setCallsLastFetch(callsData.last_fetch ? new Date(callsData.last_fetch) : null);
      
      const realCalls = (callsData.calls || []).map(call => ({
        id: call.callid,
        company: call.company || "Unknown",
        phone: call.direction === "IN" ? call.origin : call.destination,
        status: call.status === "Connected." ? "completed" : 
                call.callback_status === "overdue" ? "callback_pending" :
                call.callback_status === "pending" ? "callback_pending" :
                call.is_missed ? "missed" : "completed",
        duration: call.duration_seconds || call.duration || 0,
        waitTime: 0,
        assignedTo: (agentMappingsRes.data.agents || []).find(m => m.name === call.assigned_to)?.id || "support",
        timestamp: call.unixtime * 1000,
        direction: call.direction === "IN" ? "inbound" : "outbound",
        ticketCreated: call.needs_ticket,
        datetime: call.datetime,
        callback_status: call.callback_status
      }));
      setCalls(realCalls);
      
      // Calculate scores with real data and agent mappings
      const scores = calculateTeamScores(ticketData, realCalls, agentMappingsRes.data.agents || []);
      setTeamScores(scores);
      
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, [calculateTeamScores, seenTicketKeys, playVipAlertSound]);

  useEffect(() => {
    fetchData();
    const dataInterval = setInterval(fetchData, 60000);
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    
    return () => {
      clearInterval(dataInterval);
      clearInterval(timeInterval);
    };
  }, [fetchData]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs.toString().padStart(2, '0')}s`;
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', timeZone: 'Australia/Brisbane' });
  };

  // Calculate call metrics
  const callMetrics = {
    total: calls.length,
    waiting: calls.filter(c => c.status === "active").length,
    missed: calls.filter(c => c.status === "missed" || c.status === "callback_pending").length,
    completed: calls.filter(c => c.status === "completed" || c.status === "callback_completed").length,
    avgWaitTime: calls.length > 0 ? Math.round(calls.reduce((sum, c) => sum + c.waitTime, 0) / calls.length) : 0,
    callbackCompliance: calls.length > 0 ? 
      Math.round((calls.filter(c => c.status !== "callback_pending").length / calls.length) * 100) : 100
  };

  // Get active/recent calls for display
  const activeCalls = calls.filter(c => c.status === "active");
  const recentCalls = calls.filter(c => c.status !== "active").slice(0, 5);

  // Resolved statuses to exclude from active queues
  const resolvedStatuses = [
    "resolved", "closed", "done", "cancelled", "canceled",
    "resolved without email", "resolved with email"
  ];
  
  // Get priority tickets (exclude resolved)
  const highPriorityTickets = tickets
    .filter(t => 
      (t.priority === "Highest" || t.priority === "High") &&
      !resolvedStatuses.includes((t.status || "").toLowerCase())
    )
    .slice(0, 5);
  const idleTickets = tickets
    .filter(t => 
      t.idle_hours >= 4 &&
      !resolvedStatuses.includes((t.status || "").toLowerCase())
    )
    .sort((a, b) => b.idle_hours - a.idle_hours)
    .slice(0, 5);

  if (!stats) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <RefreshCw className="w-12 h-12 text-[#007AFF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-y-auto" data-testid="command-centre">
      {/* Audio element for VIP alerts */}
      <audio ref={audioRef} src={VIP_ALERT_SOUND} preload="auto" />
      
      {/* VIP Alert Banner */}
      {vipAlerts.length > 0 && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#FFD700] via-[#FFA500] to-[#FFD700] shadow-lg animate-pulse" data-testid="vip-alert-banner">
          <div className="container mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Crown className="w-6 h-6 text-black animate-bounce" />
                  <span className="font-bold text-black text-lg">VIP TICKET ALERT</span>
                </div>
                <div className="flex gap-2">
                  {vipAlerts.slice(0, 3).map((alert) => (
                    <div key={alert.key} className="bg-black/20 rounded-lg px-3 py-1 flex items-center gap-2">
                      <span className="font-mono text-sm text-black font-bold">{alert.key}</span>
                      <span className="text-xs text-black/80 max-w-[200px] truncate">{alert.summary}</span>
                      <button 
                        onClick={() => dismissVipAlert(alert.key)}
                        className="ml-2 text-black/60 hover:text-black"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className={`p-2 rounded-full ${soundEnabled ? 'bg-black/20 text-black' : 'bg-black/10 text-black/50'}`}
                  title={soundEnabled ? "Mute alerts" : "Enable sound"}
                >
                  <Volume2 className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setVipAlerts([])}
                  className="bg-black/20 text-black px-3 py-1 rounded-lg font-medium hover:bg-black/30 transition-colors"
                >
                  Dismiss All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Top Bar - Overall Health */}
      <header className={`bg-[#111111] border-b border-[#27272A] px-6 py-3 ${vipAlerts.length > 0 ? 'mt-16' : ''}`} data-testid="top-bar">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              EQUIPR COMMAND CENTRE
            </h1>
            <div className="flex items-center gap-2 text-sm text-[#30D158]">
              <span className="w-2 h-2 rounded-full bg-[#30D158] animate-pulse" />
              LIVE
            </div>
          </div>
          
          {/* Health Metrics */}
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <Phone className="w-5 h-5 text-[#007AFF]" />
              <span className="text-[#A1A1AA] text-sm">Calls Today:</span>
              <span className="text-xl font-bold">
                {(() => {
                  // Check if any calls are from today (AEST timezone)
                  const now = new Date();
                  const todayAEST = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })).toDateString();
                  const todaysCalls = calls.filter(c => {
                    if (!c.datetime && !c.timestamp) return false;
                    const callDate = c.datetime ? new Date(c.datetime) : new Date(c.timestamp);
                    const callAEST = new Date(callDate.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })).toDateString();
                    return callAEST === todayAEST;
                  });
                  return todaysCalls.length || '—';
                })()}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <PhoneCall className="w-5 h-5 text-[#30D158]" />
              <span className="text-[#A1A1AA] text-sm">Active:</span>
              <span className="text-xl font-bold text-[#30D158]">{callMetrics.waiting}</span>
            </div>
            <div className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-[#007AFF]" />
              <span className="text-[#A1A1AA] text-sm">Open Tickets:</span>
              <span className="text-xl font-bold">{stats.total_open + stats.total_in_progress + stats.total_waiting}</span>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[#FF9F0A]" />
              <span className="text-[#A1A1AA] text-sm">SLA Risk:</span>
              <span className="text-xl font-bold text-[#FF9F0A]">{stats.sla_at_risk}</span>
            </div>
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-[#FF453A]" />
              <span className="text-[#A1A1AA] text-sm">P1 Active:</span>
              <span className="text-xl font-bold text-[#FF453A]">{stats.high_priority}</span>
            </div>
            <div className="flex items-center gap-2">
              <Database className={`w-5 h-5 ${
                databaseHealth?.summary?.critical > 0 ? 'text-[#FF453A]' : 
                databaseHealth?.summary?.warning > 0 ? 'text-[#FF9F0A]' : 'text-[#30D158]'
              }`} />
              <span className="text-[#A1A1AA] text-sm">DB Health:</span>
              <span className={`text-xl font-bold ${
                databaseHealth?.summary?.critical > 0 ? 'text-[#FF453A]' : 
                databaseHealth?.summary?.warning > 0 ? 'text-[#FF9F0A]' : 'text-[#30D158]'
              }`}>
                {databaseHealth?.summary?.critical > 0 ? `${databaseHealth.summary.critical} Critical` :
                 databaseHealth?.summary?.warning > 0 ? `${databaseHealth.summary.warning} Warning` :
                 databaseHealth?.summary?.healthy > 0 ? 'Healthy' : '-'}
              </span>
            </div>
            <div className="flex items-center gap-2" data-testid="cc-web-service-health">
              <Server className={`w-5 h-5 ${
                wcfHealth?.summary?.critical > 0 ? 'text-[#FF453A]' : 
                wcfHealth?.summary?.warning > 0 ? 'text-[#FF9F0A]' : 'text-[#30D158]'
              }`} />
              <span className="text-[#A1A1AA] text-sm">Web Services:</span>
              <span className={`text-xl font-bold ${
                wcfHealth?.summary?.critical > 0 ? 'text-[#FF453A]' : 
                wcfHealth?.summary?.warning > 0 ? 'text-[#FF9F0A]' : 'text-[#30D158]'
              }`}>
                {wcfHealth?.summary?.critical > 0 ? `${wcfHealth.summary.critical} Critical` :
                 wcfHealth?.summary?.warning > 0 ? `${wcfHealth.summary.warning} Warning` :
                 wcfHealth?.summary?.healthy > 0 ? 'Healthy' : '-'}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {currentTime.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Australia/Brisbane' })}
              </div>
              <div className="text-xs text-[#A1A1AA]">
                {currentTime.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Australia/Brisbane' })} AEST
              </div>
            </div>
            <button
              onClick={toggleFullscreen}
              className="p-2 hover:bg-[#1A1A1A] rounded transition-colors"
              data-testid="fullscreen-btn"
            >
              <Maximize className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Grid */}
      <div className="grid grid-cols-12 gap-4 p-4 h-[calc(100vh-120px)]">
        
        {/* Left Panel - Call KPIs & Critical Clients */}
        <div className="col-span-3 flex flex-col gap-4" data-testid="calls-panel">
          {/* Call KPI Cards */}
          <div className="grid grid-cols-2 gap-3">
            {(() => {
              // Check if calls are from today (AEST timezone)
              const now = new Date();
              const todayAEST = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })).toDateString();
              const todaysCalls = calls.filter(c => {
                if (!c.datetime && !c.timestamp) return false;
                const callDate = c.datetime ? new Date(c.datetime) : new Date(c.timestamp);
                const callAEST = new Date(callDate.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })).toDateString();
                return callAEST === todayAEST;
              });
              const hasDataToday = todaysCalls.length > 0;
              const todaysInbound = todaysCalls.filter(c => c.direction === 'inbound').length;
              const todaysOutbound = todaysCalls.filter(c => c.direction === 'outbound').length;
              const todaysMissed = todaysCalls.filter(c => c.status === 'missed' || c.status === 'callback_pending').length;
              const todaysAvgDuration = todaysCalls.length > 0 
                ? Math.round(todaysCalls.reduce((sum, c) => sum + (c.duration || 0), 0) / todaysCalls.length) 
                : 0;
              
              return (
                <>
                  {/* Inbound Calls Card */}
                  <div className="bg-[#111111] border border-[#27272A] rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <PhoneIncoming className="w-5 h-5 text-[#30D158]" />
                      <span className="text-xs text-[#A1A1AA]">INBOUND</span>
                    </div>
                    <p className="text-3xl font-bold text-white">
                      {hasDataToday ? todaysInbound : '—'}
                    </p>
                    <div className="mt-2 flex items-center gap-1">
                      {hasDataToday ? (
                        <>
                          <span className={`text-lg font-bold ${
                            todaysInbound > 0 
                              ? Math.round(((todaysInbound - todaysMissed) / todaysInbound) * 100) >= 90
                                ? 'text-[#30D158]'
                                : 'text-[#FF9F0A]'
                              : 'text-[#A1A1AA]'
                          }`}>
                            {todaysInbound > 0 
                              ? Math.round(((todaysInbound - todaysMissed) / todaysInbound) * 100)
                              : 100}%
                          </span>
                          <span className="text-xs text-[#A1A1AA]">answered</span>
                        </>
                      ) : (
                        <span className="text-xs text-[#71717A]">No data today</span>
                      )}
                    </div>
                  </div>
                  
                  {/* Outbound Calls Card */}
                  <div className="bg-[#111111] border border-[#27272A] rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <PhoneOutgoing className="w-5 h-5 text-[#007AFF]" />
                      <span className="text-xs text-[#A1A1AA]">OUTBOUND</span>
                    </div>
                    <p className="text-3xl font-bold text-white">
                      {hasDataToday ? todaysOutbound : '—'}
                    </p>
                    <div className="mt-2 flex items-center gap-1">
                      {hasDataToday ? (
                        <>
                          <span className="text-lg font-bold text-[#007AFF]">
                            {Math.floor(todaysAvgDuration / 60)}m
                          </span>
                          <span className="text-xs text-[#A1A1AA]">avg duration</span>
                        </>
                      ) : (
                        <span className="text-xs text-[#71717A]">No data today</span>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>
          
          {/* Callbacks Alert - only show if there are pending callbacks from today */}
          {(() => {
            const now = new Date();
            const todayAEST = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })).toDateString();
            const todaysCallbacks = calls.filter(c => {
              if (c.status !== 'callback_pending' && c.callback_status !== 'pending' && c.callback_status !== 'overdue') return false;
              if (!c.datetime && !c.timestamp) return false;
              const callDate = c.datetime ? new Date(c.datetime) : new Date(c.timestamp);
              const callAEST = new Date(callDate.toLocaleString('en-US', { timeZone: 'Australia/Brisbane' })).toDateString();
              return callAEST === todayAEST;
            });
            
            const overdueCount = todaysCallbacks.filter(c => c.callback_status === 'overdue').length;
            const pendingCount = todaysCallbacks.length - overdueCount;
            
            if (todaysCallbacks.length === 0) return null;
            
            return (
              <div className={`border rounded-lg p-3 ${
                overdueCount > 0 
                  ? 'bg-[#FF453A]/10 border-[#FF453A]/30'
                  : 'bg-[#FF9F0A]/10 border-[#FF9F0A]/30'
              }`}>
                <div className="flex items-center gap-2">
                  <PhoneMissed className={`w-5 h-5 ${
                    overdueCount > 0 ? 'text-[#FF453A]' : 'text-[#FF9F0A]'
                  }`} />
                  <span className={`font-medium ${
                    overdueCount > 0 ? 'text-[#FF453A]' : 'text-[#FF9F0A]'
                  }`}>
                    {overdueCount > 0 
                      ? `${overdueCount} Overdue Callbacks!`
                      : `${pendingCount} Callbacks Pending`}
                  </span>
                </div>
                <p className="text-xs text-[#A1A1AA] mt-1">Return within 30 minutes</p>
              </div>
            );
          })()}

          {/* Critical Clients */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-4 flex-1 overflow-hidden">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-[#FF453A]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>CRITICAL CLIENTS</h2>
              <span className="ml-auto text-sm text-[#FF453A] font-bold">
                {(companyHealth?.companies || []).filter(c => c.status === 'critical').length}
              </span>
            </div>
            
            <div className="space-y-2 overflow-y-auto" style={{ maxHeight: 'calc(100% - 50px)' }}>
              {(companyHealth?.companies || [])
                .filter(c => c.status === 'critical' || c.status === 'warning')
                .slice(0, 8)
                .map((company, idx) => (
                  <div 
                    key={idx} 
                    className={`bg-[#1A1A1A] rounded-lg p-3 border-l-4 ${
                      company.status === 'critical' ? 'border-[#FF453A]' : 'border-[#FF9F0A]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm truncate" title={company.company}>
                        {company.company || 'Unknown'}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        company.status === 'critical' 
                          ? 'bg-[#FF453A]/20 text-[#FF453A]'
                          : 'bg-[#FF9F0A]/20 text-[#FF9F0A]'
                      }`}>
                        {company.health_score}%
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#A1A1AA]">
                      {company.sla_breached > 0 && (
                        <span className="text-[#FF453A]">{company.sla_breached} SLA breach</span>
                      )}
                      {company.high_priority > 0 && (
                        <span className="text-[#FF9F0A]">{company.high_priority} high priority</span>
                      )}
                      {company.idle_tickets > 0 && (
                        <span>{company.idle_tickets} idle</span>
                      )}
                    </div>
                    {/* Health bar */}
                    <div className="mt-2 h-1 bg-[#27272A] rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${
                          company.health_score >= 80 ? 'bg-[#30D158]' :
                          company.health_score >= 50 ? 'bg-[#FF9F0A]' : 'bg-[#FF453A]'
                        }`}
                        style={{ width: `${company.health_score}%` }}
                      />
                    </div>
                  </div>
              ))}
              
              {(companyHealth?.companies || []).filter(c => c.status === 'critical' || c.status === 'warning').length === 0 && (
                <div className="text-center py-8">
                  <CheckCircle className="w-8 h-8 text-[#30D158] mx-auto mb-2" />
                  <p className="text-sm text-[#A1A1AA]">All clients healthy</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Centre - JIRA Ticket Queue */}
        <div className="col-span-5 flex flex-col gap-4" data-testid="tickets-panel">
          {/* High Priority Tickets */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-4 flex-1">
            <div className="flex items-center gap-2 mb-4">
              <Flame className="w-5 h-5 text-[#FF453A]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>PRIORITY QUEUE</h2>
              <span className="ml-auto text-sm text-[#FF453A] font-bold">{highPriorityTickets.length}</span>
            </div>
            
            {highPriorityTickets.length > 0 ? (
              <div className="space-y-2">
                {highPriorityTickets.map(ticket => {
                  const orgKey = (ticket.organization || "").toLowerCase();
                  const clientInfo = vipLookup[orgKey];
                  return (
                  <div key={ticket.key} className={`bg-[#1A1A1A] rounded-lg p-3 border-l-4 ${
                    clientInfo?.is_vip ? 'border-[#FFD700]' : 'border-[#FF453A]'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <a 
                            href={`${JIRA_URL}/${ticket.key}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#007AFF] hover:underline font-mono text-sm"
                          >
                            {ticket.key}
                          </a>
                          {clientInfo?.is_vip && (
                            <Crown className="w-4 h-4 text-[#FFD700]" title="VIP Client" />
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            ticket.priority === "Highest" ? "bg-[#FF453A]/20 text-[#FF453A]" : "bg-[#FF9F0A]/20 text-[#FF9F0A]"
                          }`}>
                            {ticket.priority}
                          </span>
                          {ticket.sla_breached && (
                            <span className="text-xs px-2 py-0.5 rounded bg-[#FF453A]/20 text-[#FF453A] animate-pulse">
                              SLA BREACH
                            </span>
                          )}
                        </div>
                        <p className="text-sm mt-1 truncate">{ticket.summary}</p>
                      </div>
                      <div className="text-right ml-4">
                        <p className="text-sm font-medium">{ticket.assignee?.split(' ')[0] || "Unassigned"}</p>
                        <p className="text-xs text-[#A1A1AA]">{ticket.idle_hours?.toFixed(1)}h idle</p>
                      </div>
                    </div>
                  </div>
                )})}
              </div>
            ) : (
              <div className="text-center py-8 text-[#A1A1AA]">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-[#30D158]" />
                <p>No high priority tickets!</p>
              </div>
            )}
          </div>
          
          {/* Idle/Ageing Tickets */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-4 flex-1">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-5 h-5 text-[#FF9F0A]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>IDLE TICKETS</h2>
              <span className="ml-auto text-sm text-[#FF9F0A] font-bold">{stats.idle_tickets}</span>
            </div>
            
            <div className="space-y-2">
              {idleTickets.map(ticket => (
                <div key={ticket.key} className="bg-[#1A1A1A] rounded-lg p-3 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <a 
                        href={`${JIRA_URL}/${ticket.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#007AFF] hover:underline font-mono text-sm"
                      >
                        {ticket.key}
                      </a>
                      <span className="text-xs text-[#A1A1AA]">{ticket.status}</span>
                    </div>
                    <p className="text-sm truncate">{ticket.summary}</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-lg font-bold text-[#FF9F0A]">{ticket.idle_hours?.toFixed(0)}h</p>
                    <p className="text-xs text-[#A1A1AA]">{ticket.assignee?.split(' ')[0] || "Unassigned"}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Panel - Team Activity & Leaderboard */}
        <div className="col-span-4 flex flex-col gap-4" data-testid="team-panel">
          {/* Agent Health Meters (replaces gamification leaderboard) */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-4" data-testid="agent-health-panel">
            <div className="flex items-center gap-2 mb-4">
              <HeartPulse className="w-5 h-5 text-[#30D158]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>AGENT HEALTH</h2>
              <span className="ml-auto text-xs text-[#71717A]">Queue Health · worst first</span>
            </div>

            <div className="space-y-2">
              {agents
                .filter(a => a.name && a.name !== "Unassigned" && a.queue_health)
                .sort((a, b) => a.queue_health.score - b.queue_health.score)
                .map(agent => (
                  <div key={agent.name} className="flex items-center gap-3 bg-[#1A1A1A] rounded-lg p-3" data-testid={`agent-health-${agent.name}`}>
                    <QueueHealthGauge score={agent.queue_health.score} color={agent.queue_health.color} size={48} stroke={5} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{agent.name}</span>
                        <span className="text-xs font-medium" style={{ color: agent.queue_health.color }}>{agent.queue_health.label}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-[#A1A1AA] mt-1">
                        <span>{agent.open_tickets} open</span>
                        <span className="text-[#FF453A]">{agent.high_priority} high</span>
                        <span className="text-[#64D2FF]">{agent.avg_idle_hours}h idle</span>
                      </div>
                    </div>
                    {agent.queue_health.needs_action > 0 && (
                      <span className="text-xs bg-[#FF9F0A]/15 text-[#FF9F0A] px-2 py-1 rounded-full shrink-0">
                        {agent.queue_health.needs_action} to action
                      </span>
                    )}
                  </div>
                ))}
              {agents.filter(a => a.name && a.name !== "Unassigned" && a.queue_health).length === 0 && (
                <p className="text-sm text-[#71717A] text-center py-4">No agent data available.</p>
              )}
            </div>
          </div>
          
          {/* Team Workload */}
          <div className="bg-[#111111] border border-[#27272A] rounded-lg p-4 flex-1">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-[#007AFF]" />
              <h2 className="text-lg font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>TEAM WORKLOAD</h2>
            </div>
            
            <div className="space-y-4">
              {teamScores.map(member => (
                <div key={member.id} className="bg-[#1A1A1A] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{ backgroundColor: member.color + '30', color: member.color }}
                      >
                        {member.avatar}
                      </div>
                      <span className="font-medium">{member.name.split(' ')[0]}</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded ${
                      member.status === "on_call" ? "bg-[#30D158]/20 text-[#30D158]" :
                      member.status === "busy" ? "bg-[#FF9F0A]/20 text-[#FF9F0A]" :
                      "bg-[#007AFF]/20 text-[#007AFF]"
                    }`}>
                      {member.status === "on_call" ? "On Call" : 
                       member.status === "busy" ? "Busy" : "Available"}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <p className="text-lg font-bold">{member.openTickets}</p>
                      <p className="text-xs text-[#A1A1AA]">Open</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-[#30D158]">{member.slaCompliance}%</p>
                      <p className="text-xs text-[#A1A1AA]">SLA</p>
                    </div>
                    <div>
                      <p className="text-lg font-bold">{member.avgResponseTime}m</p>
                      <p className="text-xs text-[#A1A1AA]">Avg Resp</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar - Daily Metrics */}
      <footer className="fixed bottom-0 left-0 right-0 bg-[#111111] border-t border-[#27272A] px-6 py-3" data-testid="bottom-bar">
        <div className="flex items-center justify-around">
          <div className="text-center">
            <p className="text-2xl font-bold">{callMetrics.avgWaitTime}s</p>
            <p className="text-xs text-[#A1A1AA]">Avg Wait Time</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-[#FF453A]">{callMetrics.missed}</p>
            <p className="text-xs text-[#A1A1AA]">Missed Calls</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-[#30D158]">{callMetrics.callbackCompliance}%</p>
            <p className="text-xs text-[#A1A1AA]">Callback Compliance</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold">{calls.filter(c => c.ticketCreated).length}/{callMetrics.completed}</p>
            <p className="text-xs text-[#A1A1AA]">Tickets From Calls</p>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-bold ${stats.sla_breached > 0 ? 'text-[#FF453A]' : 'text-[#30D158]'}`}>
              {stats.total_open + stats.total_in_progress + stats.total_waiting > 0 
                ? Math.round(((stats.total_open + stats.total_in_progress + stats.total_waiting - stats.sla_breached) / 
                   (stats.total_open + stats.total_in_progress + stats.total_waiting)) * 100)
                : 100}%
            </p>
            <p className="text-xs text-[#A1A1AA]">SLA Compliance</p>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-bold ${(companyHealth?.summary?.critical || 0) > 0 ? 'text-[#FF453A] animate-pulse' : 'text-[#30D158]'}`}>
              {companyHealth?.summary?.critical || 0}
            </p>
            <p className="text-xs text-[#A1A1AA]">Critical Clients</p>
          </div>
          <div className="text-center">
            <p className={`text-2xl font-bold ${agents.filter(a => a.name !== 'Unassigned' && a.queue_health && a.queue_health.score < 50).length > 0 ? 'text-[#FF453A]' : 'text-[#30D158]'}`}>
              {agents.filter(a => a.name !== 'Unassigned' && a.queue_health && a.queue_health.score < 50).length}
            </p>
            <p className="text-xs text-[#A1A1AA]">Agents At Risk</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
